import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useChatStore } from '@stores/chat.store';
import { useChatConversations } from '@hooks/api/chat-websocket';
import { useAdvertiserStore } from '@stores/advertiser';
import { useAnalytics } from '@hooks/analytics';
import { useConversationsUpdate, useAnalyticsResponseTime } from '@hooks/chat';
import { useDeviceSize } from '@hooks/device-size';
import { formatStatusMessage } from '@/utils/helpers';

import type { TChatPageConfig, TChatPageLogicResult } from './chat.types';
import { TAB_NAMES, DEFAULT_TAB_NAME } from './utils/chat.constants';

/** Orquestra estado da página de chat: tabs, analytics de pageview e flags de visibilidade mobile/desktop. */
export const useChatPageLogic = ({ selectedTab = 1 }: TChatPageConfig): TChatPageLogicResult => {
	const { setConversationTabs } = useChatConversations();
	const conversationActive = useChatStore(s => s.conversation.active);
	const messageListFetched = useChatStore(s => s.messageList.fetched);
	const messageListSource = useChatStore(s => s.messageList.source);
	const storeSelectedTabIndex = useChatStore(s => s.tabs.conversation);
	useConversationsUpdate();
	useAnalyticsResponseTime();
	const isConversationSelected = !!conversationActive;
	const { isMobile } = useDeviceSize();
	const advertiserState = useAdvertiserStore(useShallow((s) => ({ source: s.source, fetched: s.fetched })));
	const { trackPageView } = useAnalytics();

	useEffect(() => {
		setConversationTabs(selectedTab);
	}, [selectedTab, setConversationTabs]);

	const tabName = TAB_NAMES[storeSelectedTabIndex] ?? DEFAULT_TAB_NAME;

	useEffect(() => {
		if (!advertiserState.source?.Retorno) return;
		trackPageView({
			pageName: `/webmotors/garagem/novo-chat/${tabName}`,
			pageType: `chat-${tabName}`,
			flowType: 'garagem',
			tier: 3,
			recurringEvent: true,
		});
	}, [tabName, advertiserState.source?.Retorno, trackPageView]);

	const firstMessage = messageListSource?.[0];
	const statusConversa = useMemo(
		() => formatStatusMessage(firstMessage?.statusMessage, !!firstMessage?.itsMyMessage),
		[firstMessage?.statusMessage, firstMessage?.itsMyMessage],
	);

	useEffect(() => {
		if (!conversationActive || !messageListFetched || !messageListSource) return;
		trackPageView({
			pageName: `/webmotors/garagem/novo-chat/${tabName}/conversa`,
			pageType: `chat-${tabName}`,
			flowType: 'garagem',
			tier: 3,
			recurringEvent: true,
			user: statusConversa ? { statusConversa } : undefined,
		});
	}, [conversationActive, messageListFetched, messageListSource, tabName, statusConversa, trackPageView]);

	const withoutConversationOrIsDesktop = !isConversationSelected || !isMobile;
	const withConversationOrIsDesktop = isConversationSelected || !isMobile;
	const withoutConversationOnMobile = isConversationSelected && isMobile;

	return {
		withoutConversationOrIsDesktop,
		withConversationOrIsDesktop,
		withoutConversationOnMobile,
	};
};
